import React, { FC, RefObject, useEffect } from 'react'
import { createPortal } from 'react-dom'
import DamVideo from '@components/DamVideo'
import { Hotspots } from '@components/HotspotsSlider/HotspotsTypes'
import ResponsiveImage from '@components/ResponsiveImage'
import { useOnEventOutside } from '@hooks/useOnEventOutside'
import { isMobile } from '@utils/mediaQueries'
import FocusTrap from 'focus-trap-react'
const smallScreen = isMobile()

const THUMBNAILS_HEIGHT = 48
const PIN_WIDTH = 48
const MARGIN = 8

function getHeaderHeight() {
  return document.querySelector('.cmp-header')?.clientHeight || 0
}

function positionSmallScreenTeaser(sliderRect: DOMRect, headerHeight: number) {
  const { scrollY } = window
  window.scrollTo({ top: sliderRect.top + scrollY - headerHeight, left: 0 })
  return { top: sliderRect.top + scrollY + sliderRect.height / 3, left: 0 }
}

function positionDesktopTeaser(
  sliderRect: DOMRect,
  modalRect: DOMRect,
  modalElement: HTMLDivElement,
  hotspot: Hotspots,
) {
  const { scrollX, scrollY } = window
  const top = Number(hotspot.positionVertical)
  const left = Number(hotspot.positionHorizontal)

  const positionTop =
    top < 50
      ? sliderRect.top + scrollY + sliderRect.height * (top / 100)
      : sliderRect.top + scrollY + sliderRect.height * (top / 100) - modalElement.offsetHeight + THUMBNAILS_HEIGHT

  // make sure that the hotspot teaser will always stay in the borders of the parent component
  const clampedTop = Math.max(
    sliderRect.top + scrollY,
    Math.min(positionTop, sliderRect.bottom + scrollY - modalRect.height),
  )

  const clampedLeft =
    left > 55
      ? sliderRect.left + scrollX + sliderRect.width * (left / 100) - (modalElement.offsetWidth + MARGIN)
      : sliderRect.left + scrollX + sliderRect.width * (left / 100) + PIN_WIDTH + MARGIN

  return { top: clampedTop, left: clampedLeft }
}

function navigateFocusable(event: React.KeyboardEvent, container: HTMLDivElement | null) {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  event.preventDefault()

  const focusable = [
    ...(container?.querySelectorAll<HTMLElement>('[tabindex="0"], button:not([disabled]), a[href]') ?? []),
  ]
  const currentIndex = focusable.indexOf(document.activeElement as HTMLElement)
  if (currentIndex === -1) return

  const nextIndex =
    event.key === 'ArrowDown'
      ? (currentIndex + 1) % focusable.length
      : (currentIndex - 1 + focusable.length) % focusable.length

  focusable[nextIndex].focus()
}

function positionTeaser(sliderElement: HTMLDivElement, modalElement: HTMLDivElement, hotspot: Hotspots) {
  const sliderPosition = sliderElement.getBoundingClientRect()
  const modalPosition = modalElement.getBoundingClientRect()

  const position = smallScreen
    ? positionSmallScreenTeaser(sliderPosition, getHeaderHeight())
    : positionDesktopTeaser(sliderPosition, modalPosition, modalElement, hotspot)

  modalElement.style.top = `${position.top}px`
  modalElement.style.left = `${position.left}px`

  const isModalOverflown = modalElement.scrollHeight > modalElement.clientHeight
  if (isModalOverflown && !smallScreen) modalElement.style.top = `${window.scrollY}px`

  modalElement.style.opacity = '1'
}

function HotspotTeaserMedia({ hotspot }: { hotspot: Hotspots }) {
  if (hotspot.isVideo && hotspot.video?.url != null) {
    return (
      <DamVideo
        videoUrl={hotspot.video.url}
        isEditModeBoolean={false}
        isVideoPlaying
        loopVideo="true"
        hideVideoControls="true"
      />
    )
  }
  return <ResponsiveImage src={hotspot.image.imageUrl || ''} alt={hotspot.image.altText || ''} />
}

function HotspotTeaserLink({ hotspot }: { hotspot: Hotspots }) {
  if (!hotspot.link || !hotspot.linkLabel) return null
  return (
    <a
      className="cmp-hotspot-teaser__description-link"
      href={hotspot.link?.externalizedURL}
      target={hotspot.link?.isTargertBlank && '_blank'}
      rel={hotspot.link?.htmlAttributes?.rel || 'nofollow'}
    >
      {hotspot.linkLabel}
    </a>
  )
}

interface HotspotProperties {
  hotspot: Hotspots
  onHotspotClick?: (index: number) => void
  isVisible: boolean
  onCloseClick: (index: number | null) => void
  ariaLabelCloseButton?: string
  sliderRef: RefObject<HTMLDivElement>
}

export const HotspotTeaser: FC<HotspotProperties> = ({
  hotspot,
  isVisible,
  onCloseClick,
  ariaLabelCloseButton,
  sliderRef,
}) => {
  const hasHotspotsImage = Boolean(hotspot?.image?.imageUrl || hotspot?.image?.originalImageUrl)
  const hasHotspotVideo = hotspot?.isVideo

  const handleClose = () => {
    onCloseClick(null)
  }

  const modal = useOnEventOutside({
    eventType: 'click',
    callBack: handleClose,
    shouldHandleKeyUp: true,
  }) as RefObject<HTMLDivElement>

  useEffect(() => {
    const canPosition = sliderRef.current !== null && modal.current !== null && isVisible
    if (canPosition) positionTeaser(sliderRef.current as HTMLDivElement, modal.current as HTMLDivElement, hotspot)
  }, [isVisible, hotspot, sliderRef])

  if (!isVisible) return null

  return createPortal(
    <>
      <div className="cmp-hotspot-teaser__backdrop" />
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- keydown drives ARIA dialog focus management (arrow-key navigation between focusable content) */}
      <div
        className="cmp-hotspot-teaser"
        ref={modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hotspot-teaser-title"
        onKeyDown={(event) => navigateFocusable(event, modal.current)}
      >
        <FocusTrap
          active={isVisible}
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: false,
            returnFocusOnDeactivate: true,
            fallbackFocus: () => modal.current || document.body,
            initialFocus: '.cmp-hotspot-teaser__description-title',
          }}
        >
          <div className="cmp-hotspot-teaser__content">
            <button className="cmp-hotspot-teaser__close" onClick={handleClose} aria-label={ariaLabelCloseButton}>
              <i className="ri-close-line"></i>
            </button>
            {(hasHotspotVideo || hasHotspotsImage) && (
              <div className="cmp-hotspot-teaser__media">
                <HotspotTeaserMedia hotspot={hotspot} />
              </div>
            )}
            <div className="cmp-hotspot-teaser__description">
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
              <p id="hotspot-teaser-title" className="cmp-hotspot-teaser__description-title" tabIndex={0}>
                {hotspot.title}
              </p>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
              <p className="cmp-hotspot-teaser__description-text" tabIndex={0}>
                {hotspot.text}
              </p>
              <HotspotTeaserLink hotspot={hotspot} />
            </div>
          </div>
        </FocusTrap>
      </div>
    </>,
    document.body,
  )
}
